import PatternProductsPreview from '@/components/PatternProducts/PatternProductsPreview.vue';
import { mountCoreVueApp } from '@/entrypoints/shared/core-vue-app';
import { usePatternProductsStore } from '@/shared/pinia/modules/pattern-products';
import { useProductStore } from '@/shared/pinia/modules/product';
import { isMobileView, isTabletView } from '@/shared/utils/dom';
import { contextualError } from '@/shared/utils/error';

import { CountryCode } from '../../__generated__/storefront/graphql';
import { PatternProduct } from '../../shared/composables/patternProducts/patternProducts.types';
import { patternSuggestionsForYarnQuery } from '../../shared/composables/yarnPatterns/yarnPatternsGateway.queries';
import { searchPatternsBySKUsQuery } from '../../shared/composables/yarnPatterns/yarnPatternsStorefront.queries';
import { getApolloClients } from '../../shared/utils/apollo-client';

const initTabCounters = async () => {
  const galleryTabs = document.querySelectorAll('.js-gallery-button-label');
  const qaTabs = document.querySelectorAll('.js-qa-button-label');

  if (!galleryTabs.length && !qaTabs.length) return;

  const productStore = useProductStore();

  try {
    const { data } = await productStore.setTabCounters();

    galleryTabs.forEach((galleryTab) => {
      galleryTab.innerHTML = galleryTab.innerHTML.replace(
        '0',
        String(data.community?.gallery?.total ?? 0)
      );
    });

    qaTabs.forEach((qaTab) => {
      qaTab.innerHTML = qaTab.innerHTML.replace(
        '0',
        String(data.community?.qa?.total ?? 0)
      );
    });
  } catch (error) {
    console.error(error);
  }
};

const initYarnPatternsTabCount = async () => {
  const yarnPatternsTabs = document.querySelectorAll(
    '.js-yarn-patterns-button-label'
  );

  if (!yarnPatternsTabs.length) return;

  const productStore = useProductStore();
  const productGroupId = productStore.legacyId;

  if (!productGroupId) return;

  try {
    // Get apollo clients
    const { gateway: gatewayClient, storefront: storefrontClient } =
      await getApolloClients();

    // Step 1: Fetch pattern SKUs from Gateway API
    const gatewayResult = await gatewayClient.query({
      query: patternSuggestionsForYarnQuery,
      variables: {
        yarnProductGroupIdentifier: productGroupId,
        locale: window?.Shopify?.locale || 'en',
        limit: 50
      }
    });

    const patterns =
      gatewayResult.data?.crafter?.patternSuggestionsForYarn?.patterns || [];

    if (patterns.length === 0) {
      updateYarnPatternsTabCount(0);
      return;
    }

    // Step 2: Search for products by pattern SKUs in Shopify
    const skus = patterns.map((p) => p.sku).filter(Boolean);
    if (skus.length === 0) {
      updateYarnPatternsTabCount(0);
      return;
    }

    const searchQuery = skus.map((sku) => `variants.sku:${sku}`).join(' OR ');
    const storefrontResult = await storefrontClient.query({
      query: searchPatternsBySKUsQuery,
      variables: {
        searchQuery,
        country: (window?.Shopify?.country as CountryCode) || CountryCode.Us
      }
    });

    const searchProducts = storefrontResult.data?.search?.nodes || [];

    // Step 3: Count how many patterns have matching products
    let matchedCount = 0;
    patterns.forEach((gp) => {
      // Filter to only Product results (exclude Article and Page)
      const products = searchProducts.filter(
        (
          result
        ): result is Extract<typeof result, { __typename?: 'Product' }> =>
          result.__typename === 'Product'
      );
      const product = products.find((p) => {
        const metafieldMatch = p.metafields?.find(
          (m) => m?.key === 'sku' && m?.value === gp.sku
        );
        if (metafieldMatch) return true;
        const variantMatch = p.variants?.nodes?.find((v) => v.sku === gp.sku);
        return !!variantMatch;
      });
      if (product) matchedCount++;
    });

    updateYarnPatternsTabCount(matchedCount);
  } catch (error) {
    console.error('Error fetching yarn patterns count:', error);
    updateYarnPatternsTabCount(0);
  }
};

const initPatternProductsPreview = async () => {
  let isMounted = false;

  const checkAndMount = async () => {
    const shouldMount =
      isMobileView(window.innerWidth) || isTabletView(window.innerWidth);

    if (shouldMount && !isMounted) {
      const productSuggestionsPreviewContainer =
        document.querySelector<HTMLDivElement>(
          '.js-pattern-products-skeletons'
        );
      if (!productSuggestionsPreviewContainer) return;

      const defaultPatternProducts =
        productSuggestionsPreviewContainer.dataset.defaultPatternProducts;
      if (!defaultPatternProducts) return;

      try {
        const initialPatternProducts: PatternProduct[] = JSON.parse(
          defaultPatternProducts
        );

        const patternProductsStore = usePatternProductsStore();
        patternProductsStore.initialize(initialPatternProducts);

        await mountCoreVueApp(
          PatternProductsPreview,
          productSuggestionsPreviewContainer
        );

        isMounted = true;

        // Clean up the html and remove the default-pattern-products attribute
        delete productSuggestionsPreviewContainer.dataset
          .defaultPatternProducts;
      } catch (error) {
        throw contextualError('product-tab-async', error, {
          message: `Unable to mount PatternProductsPreview`
        });
      }
    }
  };

  await checkAndMount();

  // Add resize listener to handle viewport changes
  const handleResize = async () => {
    await checkAndMount();
  };

  window.addEventListener('resize', handleResize);

  // Cleanup on page unload
  window.addEventListener('beforeunload', () =>
    window.removeEventListener('resize', handleResize)
  );
};

const updateYarnPatternsTabCount = (count: number) => {
  const yarnPatternsTabs = document.querySelectorAll(
    '.js-yarn-patterns-button-label'
  );

  yarnPatternsTabs.forEach((tab) => {
    // Replace the (0) with the actual count
    tab.innerHTML = tab.innerHTML.replace(/\(\d+\)/, `(${count})`);
  });
};

export {
  initPatternProductsPreview,
  initTabCounters,
  initYarnPatternsTabCount
};
